import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryVisuals = {
    tshirts: { pattern: 'TEES', gradient: 'from-zinc-900 to-lime/5' },
    hoodies: { pattern: 'HOOD', gradient: 'from-indigo-950/50 to-zinc-900' },
};

export const Categories = () => {
    const [categories, setCategories] = useState([]);

    // Fixed categories - only T-Shirts and Hoodies
    const fixedCategories = [
        { slug: 'tshirts', name: 'T-Shirts', product_count: 120 },
        { slug: 'hoodies', name: 'Hoodies', product_count: 45 },
    ];

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await axios.get(`${API}/categories`);
                // Filter to only show tshirts and hoodies
                const filtered = response.data.filter(c => 
                    c.slug === 'tshirts' || c.slug === 'hoodies'
                );
                setCategories(filtered.length > 0 ? filtered : fixedCategories);
            } catch (error) {
                console.error('Error fetching categories:', error);
                setCategories(fixedCategories);
            }
        };
        fetchCategories();
    }, []);

    return (
        <section className="py-16 md:py-24" data-testid="categories-section">
            <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                {/* Header */}
                <div className="flex justify-between items-end mb-8 md:mb-12">
                    <div>
                        <span className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2 block">
                            EXPLORE
                        </span>
                        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl">
                            SHOP<br />EVERYTHING
                        </h2>
                    </div>
                    <Link
                        to="/products"
                        className="text-xs font-medium tracking-[0.1em] uppercase text-lime border-b border-lime pb-1 hover:tracking-[0.15em] transition-all hidden md:block"
                    >
                        VIEW ALL →
                    </Link>
                </div>

                {/* Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {categories.map((category, index) => {
                        const visual = categoryVisuals[category.slug] || { pattern: category.name.slice(0, 4).toUpperCase(), gradient: 'from-zinc-900 to-zinc-800' };
                        const isFirst = index === 0;
                        
                        return (
                            <motion.div
                                key={category.slug}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className={isFirst ? 'col-span-2 md:col-span-3 lg:col-span-5' : ''}
                            >
                                <Link
                                    to={`/products?category=${category.slug}`}
                                    className={`group block relative overflow-hidden bg-card border border-border/50 hover:border-lime/50 transition-colors ${
                                        isFirst ? 'aspect-[21/8]' : 'aspect-[3/4]'
                                    }`}
                                    data-testid={`category-${category.slug}`}
                                >
                                    {/* Background gradient */}
                                    <div className={`absolute inset-0 bg-gradient-to-br ${visual.gradient}`} />
                                    
                                    {/* Pattern text */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <span className={`font-heading text-white/5 ${isFirst ? 'text-[15vw]' : 'text-[25vw] md:text-[8vw]'}`}>
                                            {visual.pattern}
                                        </span>
                                    </div>

                                    {/* Decorative circle for first card */}
                                    {isFirst && (
                                        <div className="absolute top-4 right-4 md:top-8 md:right-8 w-20 h-20 md:w-32 md:h-32 border border-lime/20 rounded-full">
                                            <div className="absolute inset-4 border border-lime/10 rounded-full" />
                                        </div>
                                    )}

                                    {/* Info overlay */}
                                    <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 bg-gradient-to-t from-black/90 to-transparent">
                                        <span className="text-[10px] tracking-[0.18em] uppercase text-lime mb-1 block">
                                            {isFirst ? 'TOP SELLERS' : 'SHOP NOW'}
                                        </span>
                                        <h3 className="font-heading text-xl md:text-2xl lg:text-3xl group-hover:text-lime transition-colors">
                                            {category.name}
                                        </h3>
                                        <span className="text-xs text-muted-foreground mt-1 block">
                                            {category.product_count}+ Styles
                                        </span>
                                    </div>

                                    {/* Hover scale effect */}
                                    <div className="absolute inset-0 bg-lime/0 group-hover:bg-lime/5 transition-colors" />
                                </Link>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};
